/** Provedor da sessão: busca a conta na API e reage a "sessão expirada". */
import { pode as podeNaMatriz } from '@metaepi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { enviar, ErroApi, ouvirSessaoExpirada, requisitar } from '../../lib/api';
import { CHAVE_CONTA, ContextoSessao, type Conta, type ValorSessao } from './contexto';

async function buscarConta(): Promise<Conta | null> {
  try {
    return await requisitar<Conta>('/auth/eu');
  } catch (erro) {
    if (erro instanceof ErroApi && erro.status === 401) return null;
    throw erro;
  }
}

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const cliente = useQueryClient();
  const { data, isPending, refetch } = useQuery({
    queryKey: CHAVE_CONTA,
    queryFn: buscarConta,
    staleTime: 60_000,
    retry: false,
  });

  const [motivoSaida, setMotivoSaida] = useState<ValorSessao['motivoSaida']>(null);

  // Se qualquer chamada disser "sessão expirada", esquece a conta (o app volta ao login)
  useEffect(
    () =>
      ouvirSessaoExpirada(() => {
        setMotivoSaida('expirada');
        cliente.setQueryData(CHAVE_CONTA, null);
      }),
    [cliente],
  );

  const sair = useCallback(async () => {
    await enviar('/auth/sair', 'POST').catch(() => undefined);
    setMotivoSaida('saiu');
    cliente.clear();
    cliente.setQueryData(CHAVE_CONTA, null);
  }, [cliente]);

  const valor = useMemo<ValorSessao>(() => {
    const conta = data ?? null;
    return {
      conta,
      carregando: isPending,
      pode: (permissao) => (conta ? podeNaMatriz(conta.usuario.perfil, permissao) : false),
      temPendencias: Boolean(
        conta && (conta.pendencias.cienciaPrivacidade || conta.pendencias.trocarSenha),
      ),
      atualizar: refetch,
      sair,
      motivoSaida,
    };
  }, [data, isPending, refetch, sair, motivoSaida]);

  return <ContextoSessao.Provider value={valor}>{children}</ContextoSessao.Provider>;
}
